/**
 * Tasks repository — the data-access home for the `tasks` slice.
 *
 * The ONLY place that touches `tasks` / `task_activity` / `task_type_costs` /
 * `visits` data. Strangler-Fig per-slice migration (manifest-6 / Track A7):
 *   Step 1 (done) — consolidated the scattered `supabase.from(...)` REST calls
 *                   + the 3 task RPCs into this repository (4 table names
 *                   centralized here; nothing else may touch them).
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/tasks/*`) + the
 *                   shared `taskSchema` / `visitSchema` / `taskActivitySchema` /
 *                   `taskTypeCostSchema` DTOs.
 *   Step 3 (HERE) — STAFF reads/writes flipped to the Nest client (reads keep a
 *                   Supabase fallback; RETURNING-dependent writes are Nest-ONLY —
 *                   see the flip scope below). The PUBLIC, unauthenticated token
 *                   flow (`getServiceRequestByToken` / `submitVisitSchedule`)
 *                   stays on Supabase — see the authz split below.
 *
 * ── Flip scope (owner-vs-staff / public-token authz split) ───────────────────
 *   FLIPPED to Nest (Supabase fallback retained): the staff dashboard READS +
 *     non-RETURNING writes — `listForCompany`, `getById`, `hardDelete`,
 *     `listForUnifiedKanban` (the tasks half of the unified board),
 *     `listListingCounts`, `statsByStatus`, `getNotes`, the task_activity
 *     list, the task_type_costs read, and the visits CRUD. All are reached only
 *     by admin/manager/employee dashboard screens that hold
 *     `tasks.{view,create,edit,delete}`.
 *   NEST-ONLY (Supabase RETURNING fallbacks retired — the 20260607220000
 *     SELECT-RLS retirement makes any `.insert()/.update().select()` 42501 for
 *     non-super-admin, so those fallbacks could only turn a Nest blip into a
 *     hard RLS error): `create`, `update`, `assignInspectionForRequest`, and
 *     the task_activity insert (`insertActivity`).
 *   KEPT on Supabase (documented):
 *     - `getServiceRequestByToken` + `submitVisitSchedule` — the PUBLIC
 *       VisitSchedule page is UNAUTHENTICATED (no JWT, no company), so it cannot
 *       send a Bearer token; a guarded Nest call would 401. The Nest module
 *       exposes these as UNGUARDED public endpoints, but the FE keeps the direct
 *       Supabase RPC here (the token is the capability).
 *
 * ── Append-only audit (task_activity) ────────────────────────────────────────
 *   `task_activity` is an append-only audit log: this repository exposes
 *   `listActivity` + `insertActivity` ONLY — never update or delete. It has no
 *   own `company_id` column; multi-tenant scope is enforced through the parent
 *   task (`task:tasks!inner(company_id)` on the read; the insert is gated by the
 *   already company-scoped task it references).
 *
 * ── Convention audit (delete semantics) ──────────────────────────────────────
 *   Neither `tasks` nor `visits` carries a `deleted_at` / `is_active` column, so
 *   there is NO soft-delete concept — the hard `.delete()` is the intended,
 *   FK-safe permanent-delete semantic (child rows cascade/null; the reversible
 *   path is the `status='cancelled'` archive in useUnifiedTasks, not a row flag).
 *   `task_type_costs` HAS `is_active`, but the only delete-shaped op is an
 *   `is_active:false` upsert (already a soft toggle) — preserved verbatim.
 *
 * Fallback-retaining methods try Nest first (only when an API is configured) and
 * fall back to the original Supabase query on any error, so local dev without the
 * Nest API keeps working for reads. The NEST-ONLY methods above are NOT
 * reversible units — their Supabase paths are dead post-RLS-retirement and were
 * deleted; they require VITE_API_URL (nestRequest throws a clear config error
 * without it). RLS is untouched. List/detail Nest responses are parsed through
 * the shared schemas (a too-strict DTO would silently fall back to Supabase —
 * the #1699 lesson).
 *
 * Deferred (cross-domain reads kept on Supabase via OTHER slices' code paths, not
 * this repo): none — every `tasks`/`task_activity`/`task_type_costs`/`visits`
 * caller in the app is routed through this repository. The dashboard-stats,
 * activity-feed, employee-portal and lead-dialog callers use the dedicated
 * methods below (`statsByStatus`, `listRecent`, `listForEmployee`, `create`,
 * `appendNotes`, `setInspectionReport`, `reactivateInspectionTask`).
 */
import { isDefinitiveNotFound, reportNestFallback } from "@/lib/nestFallback";
import { z } from "zod";
import {
  taskSchema,
  taskActivitySchema,
  taskTypeCostSchema,
  visitSchema,
  serviceRequestByTokenSchema,
  createPublicLeadInputSchema,
  type CreatePublicLeadInput,
  type ServiceRequestByToken,
} from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import { isRpcPermissionDenialError } from "@/lib/networkErrors";
import type { Database, Json } from "@/integrations/supabase/types";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";


// Authenticated Nest request bound to the "tasks" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest("tasks");

// Unauthenticated variant for public marketplace endpoints (no JWT, no session
// lookup). Used only by createLead — the public listing page has no session.
const nestRequestPublic = makeNestRequest("tasks", { auth: false });

type TaskRow = Database["public"]["Tables"]["tasks"]["Row"];
type TaskInsert = Database["public"]["Tables"]["tasks"]["Insert"];
type TaskUpdate = Database["public"]["Tables"]["tasks"]["Update"];
type VisitRow = Database["public"]["Tables"]["visits"]["Row"];
type VisitInsert = Database["public"]["Tables"]["visits"]["Insert"];
type VisitUpdate = Database["public"]["Tables"]["visits"]["Update"];

// ─── Public data shapes ──────────────────────────────────────────────────────

/**
 * Owner-portal visit row (S9) — full visit row + the property/unit display
 * labels the owner portal renders. Matches the Nest `GET /tasks/visits/owner`
 * wire shape and the Supabase fallback embed.
 */
export type OwnerVisitRow = VisitRow & {
  property: { name: string; name_en: string | null } | null;
  unit: { unit_number: string | null } | null;
};


export interface TaskListParams {
  companyId: string;
  /** Non-null = employee scoped to these property ids; null = no filter (admin). */
  assignedPropertyIds: string[] | null;
}

/**
 * A task row as the unified kanban consumes it — the own columns plus the loose
 * joined relations the board reads (`employee`/`property`/`unit`/`service_request`).
 * Relations are `unknown` so the hook's existing `as {...}` casts stay verbatim.
 */
export type KanbanTaskRow = TaskRow & {
  employee?: unknown;
  property?: unknown;
  unit?: unknown;
  service_request?: unknown;
};

/**
 * A task row as the employee portal consumes it — own columns plus the joined
 * `property`/`unit`/`contract` relations (loosely typed so the callers' existing
 * optional-chained access stays verbatim).
 */
export type EmployeeTaskRow = TaskRow & {
  property?: { id: string; name: string; name_en: string | null; address: string | null } | null;
  unit?: { id: string; unit_number: string } | null;
  contract?: { id: string; contract_number: string } | null;
};

export type { ServiceRequestByToken };

/** A single task_type_costs row (the table isn't in the generated types yet). */
export interface TaskTypeCostRow {
  id: string;
  company_id: string;
  task_type: string;
  amount: number;
  currency: string;
  is_active: boolean;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskTypeCostUpsertRow {
  company_id: string;
  task_type: string;
  amount: number;
  currency: string;
  is_active: boolean;
  note: string | null;
}

// Select strings — kept as static constants (NOT a ternary inside `.select()`)
// so Supabase's compile-time relation parser can resolve the row type.
const KANBAN_SELECT = `
  *,
  employee:employees(id, full_name, full_name_en),
  property:properties(name, name_en),
  unit:units(unit_number),
  service_request:service_requests(id, national_id, phone, property_city, property_district, property_type, service_type)
`;

const EMPLOYEE_SELECT = `
  *,
  property:properties(id, name, name_en, address),
  unit:units(id, unit_number),
  contract:contracts(id, contract_number)
`;

// ─── tasks: Supabase implementations ──────────────────────────────────────────

async function listForCompanyViaSupabase(params: TaskListParams) {
  let query = supabase
    .from("tasks")
    .select(`*, employee:employees(id, full_name, full_name_en)`)
    .eq("company_id", params.companyId);

  if (params.assignedPropertyIds && params.assignedPropertyIds.length > 0) {
    query = query.in("property_id", params.assignedPropertyIds);
  }

  const { data, error } = await query.order("created_at", { ascending: false });
  if (error) {
    console.error("Error fetching tasks:", serializeError(error));
    throw error;
  }
  return data ?? [];
}

async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from("tasks")
    .select(`*, employee:employees(id, full_name, full_name_en)`)
    .eq("id", id)
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching task:", serializeError(error));
    throw error;
  }
  return data;
}

// ─── The repository ──────────────────────────────────────────────────────────

export const tasksRepository = {
  // ── tasks ──────────────────────────────────────────────────────────────────

  /**
   * Company task list for the staff dashboard (employee-scoped by assigned
   * properties when applicable). Staff op — flipped to Nest, Supabase fallback.
   * The Nest list is company-wide; the employee `property_id` filter is applied
   * client-side after the parse so the guarded endpoint stays simple.
   */
  async listForCompany(params: TaskListParams) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.list());
        let parsed = z.array(taskSchema).parse(rows) as unknown as TaskRow[];
        if (params.assignedPropertyIds && params.assignedPropertyIds.length > 0) {
          const allowed = new Set(params.assignedPropertyIds);
          parsed = parsed.filter((t) => t.property_id && allowed.has(t.property_id));
        }
        return parsed;
      } catch (err) {
        reportNestFallback("tasks.listForCompany", err);
      }
    }
    return listForCompanyViaSupabase(params) as unknown as Promise<TaskRow[]>;
  },

  /** Single task by id, company-scoped. Staff op — flipped to Nest, fallback. */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.tasks.getById(id));
        return (row ? (taskSchema.parse(row) as unknown as TaskRow) : null);
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("tasks.getById", err);
      }
    }
    return getByIdViaSupabase(id, companyId) as unknown as Promise<TaskRow | null>;
  },

  /**
   * Insert a task. Staff op — Nest-only via the guarded POST /tasks. `company_id`
   * is injected by the caller's trusted value (never a free-form body field). Used
   * by every task-create caller (manual add, visit/contract/inquiry lead dialogs,
   * inspection assignment, visit scheduling).
   *
   * The Supabase fallback was retired: it used `.insert().select()` (INSERT ...
   * RETURNING), and 20260607220000 dropped the non-super-admin SELECT policy on
   * tasks — the RETURNING 42501s, so the fallback could only ever turn a Nest
   * blip into a hard RLS error (same failure mode as the service_requests owner
   * create, fixed the same way). A Nest error now surfaces instead of silently
   * degrading. `nestRequest` throws a clear NestRequestError when VITE_API_URL
   * is unset.
   */
  async create(companyId: string, data: Omit<TaskInsert, "company_id">) {
    const row = await nestRequest<unknown>(API_ROUTES.tasks.create(), {
      method: "POST",
      body: JSON.stringify({ ...data, company_id: companyId }),
    });
    return taskSchema.parse(row) as unknown as TaskRow;
  },

  /**
   * Update task fields, company-scoped. Staff op — Nest-only via the guarded
   * PUT /tasks/:id (scope resolved from the JWT). Returns the updated row (or
   * null if no row matched the company scope).
   *
   * Supabase fallback retired: `.update().select()` (UPDATE ... RETURNING)
   * 42501s post-20260607220000 (no non-super-admin SELECT policy on tasks).
   *
   * `companyId` is no longer read here (the retired Supabase fallback used it;
   * the guarded endpoint scopes from the JWT) — kept in the signature for
   * call-site compatibility and as the documented multi-tenant intent.
   */
  async update(id: string, _companyId: string, data: TaskUpdate) {
    const row = await nestRequest<unknown>(API_ROUTES.tasks.update(id), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    return (row ? (taskSchema.parse(row) as unknown as TaskRow) : null);
  },

  /**
   * Hard delete a task, company-scoped. Staff op — flipped to Nest, fallback.
   * FK-safe permanent delete (child rows cascade/null); no `deleted_at` exists so
   * this is the intended semantic (the reversible path is the cancelled-status
   * archive, NOT this method).
   */
  async hardDelete(id: string, companyId: string) {
    if (API_URL) {
      try {
        await nestRequest<void>(API_ROUTES.tasks.remove(id), { method: "DELETE" });
        return;
      } catch (err) {
        reportNestFallback("tasks.hardDelete", err);
      }
    }
    const { error } = await supabase
      .from("tasks")
      .delete()
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) {
      console.error("Error deleting task:", serializeError(error));
      throw error;
    }
  },

  /**
   * The tasks half of the unified kanban board — rich relation join, company
   * scoped (NO employee property filter; the unified board is admin/manager).
   * Staff op — flipped to Nest, Supabase fallback. The maintenance + nafath
   * enrichment stays orchestrated in the hook (cross-domain).
   */
  async listForUnifiedKanban(companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.list());
        return z.array(taskSchema).parse(rows) as unknown as KanbanTaskRow[];
      } catch (err) {
        reportNestFallback("tasks.listForUnifiedKanban", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select(KANBAN_SELECT)
      .eq("company_id", companyId);
    if (error) {
      console.error("Error fetching unified tasks:", serializeError(error));
      throw error;
    }
    return (data ?? []) as unknown as KanbanTaskRow[];
  },

  /**
   * Tasks assigned to one employee (rich join), company-scoped. Employee-portal
   * read used by useEmployeeData. Staff op — flipped to Nest (Track-E slice #3),
   * Supabase fallback.
   */
  async listForEmployee(employeeId: string, companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.byEmployee(employeeId));
        return rows as unknown as EmployeeTaskRow[];
      } catch (err) {
        reportNestFallback("tasks.listForEmployee", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select(EMPLOYEE_SELECT)
      .eq("assigned_to", employeeId)
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching employee tasks:", serializeError(error));
      throw error;
    }
    return (data ?? []) as unknown as EmployeeTaskRow[];
  },

  /**
   * The CALLING employee's own assigned tasks. Primary path: GET /tasks/mine —
   * the server derives the employee from the JWT, so it's self-scoped and needs
   * no tasks.view grant (an assignee must see their own work even with a custom
   * role that lacks the tasks feature).
   *
   * NOTE: `employeeId`/`companyId` are used ONLY by the Supabase fallback (which
   * filters `assigned_to = employeeId`); the primary Nest path ignores them and
   * resolves the employee server-side from the JWT. Used by the employee portal.
   */
  async listMine(employeeId: string, companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.mine());
        return rows as unknown as EmployeeTaskRow[];
      } catch (err) {
        reportNestFallback("tasks.listMine", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select(EMPLOYEE_SELECT)
      .eq("assigned_to", employeeId)
      .eq("company_id", companyId)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching my tasks:", serializeError(error));
      throw error;
    }
    return (data ?? []) as unknown as EmployeeTaskRow[];
  },

  /**
   * Recent tasks (small limit) for the dashboard activity feed. Company-scoped.
   * Staff read — flipped to Nest (Track-E slice #3), Supabase fallback.
   */
  async listRecent(companyId: string, limit = 2) {
    if (API_URL) {
      try {
        const params = new URLSearchParams({ limit: String(limit) });
        const rows = await nestRequest<unknown[]>(`${API_ROUTES.tasks.recent()}?${params.toString()}`);
        return rows as TaskRow[];
      } catch (err) {
        reportNestFallback("tasks.listRecent", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select("id, title, description, status, priority, due_date, created_at")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) {
      console.error("Error fetching recent tasks:", serializeError(error));
      throw error;
    }
    return data ?? [];
  },

  /**
   * Lightweight status-only task list for dashboard stats. Company-scoped.
   * Staff read — flipped to Nest `GET /tasks/stats` (Track-E slice #3),
   * Supabase fallback.
   */
  async listStatusesForStats(companyId: string, range?: { startDate?: string; endDate?: string }) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.stats(range?.startDate, range?.endDate));
        return rows as { id: string; status: string }[];
      } catch (err) {
        reportNestFallback("tasks.listStatusesForStats", err);
      }
    }
    let query = supabase
      .from("tasks")
      .select("id, status")
      .eq("company_id", companyId);
    if (range?.startDate) query = query.gte("created_at", range.startDate);
    if (range?.endDate) query = query.lte("created_at", range.endDate);
    const { data, error } = await query;
    if (error) {
      console.error("Error fetching task stats:", serializeError(error));
      throw error;
    }
    return data ?? [];
  },

  /**
   * Detail fetch for the employee task-details panel (property+unit join with
   * city/floor), company-scoped. Staff/portal read — flipped to Nest
   * `GET /tasks/:id/details` (Track-E slice #3), Supabase fallback.
   */
  async getDetailsById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.tasks.details(id));
        return (row ? (taskSchema.parse(row) as unknown as TaskRow) : null);
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("tasks.getDetailsById", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select(`
        *,
        property:properties(id, name, name_en, address, city),
        unit:units(id, unit_number, floor_number)
      `)
      .eq("id", id)
      .eq("company_id", companyId)
      .maybeSingle();
    if (error) {
      console.error("Error fetching task details:", serializeError(error));
      throw error;
    }
    return data;
  },

  /**
   * Detail fetch for the inspection report screen (rich join), company-scoped.
   * Staff read — flipped to Nest `GET /tasks/:id/inspection-report`
   * (Track-E slice #3), Supabase fallback.
   */
  async getForInspectionReport(id: string, companyId: string) {
    if (API_URL) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const row = await nestRequest<any>(API_ROUTES.tasks.inspectionReport(id));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return row as any;
      } catch (err) {
        reportNestFallback("tasks.getForInspectionReport", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select(
        "*, service_request:service_requests(*), property:properties(name, name_en, city, district), unit:units(unit_number)",
      )
      .eq("id", id)
      .eq("company_id", companyId)
      .maybeSingle();
    if (error) {
      console.error("Error fetching inspection task:", serializeError(error));
      throw error;
    }
    return data;
  },

  /**
   * Persist an inspection report blob onto a task, company-scoped. The column
   * isn't in the generated types yet, so cast at the boundary (baselined pattern).
   */
  async setInspectionReport(id: string, companyId: string, report: Json) {
    const { error } = await supabase
      .from("tasks")
      .update({ inspection_report: report } as unknown as TaskUpdate)
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) {
      console.error("Error saving inspection report:", serializeError(error));
      throw error;
    }
  },

  /**
   * Read a task's current notes (company-scoped) — used by the
   * append-then-write flows (feedback / add-note) so earlier notes aren't lost.
   * Staff read — flipped to Nest `GET /tasks/:id/notes` (Track-E slice #3),
   * Supabase fallback.
   */
  async getNotes(id: string, companyId: string) {
    // Sentry DALLALI-FRONTEND-DN captured `GET /tasks//notes` — an empty id
    // reaching this method builds a malformed URL, guaranteeing a fallback
    // event AND an invalid-uuid error from the Supabase read (#3368). A
    // blank id cannot have notes; answer the contract directly.
    if (!id) return null;
    if (API_URL) {
      try {
        const notes = await nestRequest<string | null>(API_ROUTES.tasks.notes(id));
        return notes ?? null;
      } catch (err) {
        reportNestFallback("tasks.getNotes", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select("notes")
      .eq("id", id)
      .eq("company_id", companyId)
      .maybeSingle();
    if (error) {
      console.error("Error fetching task notes:", serializeError(error));
      throw error;
    }
    return data?.notes ?? null;
  },

  /**
   * Re-activate the inspection task for a rejected service request (status →
   * in_progress, completed_at cleared). Matched by service_request_id +
   * task_type, company-scoped. Used by RejectRequestDialog.
   */
  async reactivateInspectionForRequest(requestId: string, companyId: string) {
    const { error } = await supabase
      .from("tasks")
      .update({ status: "in_progress", completed_at: null })
      .eq("service_request_id", requestId)
      .eq("task_type", "inspection")
      .eq("company_id", companyId);
    if (error) {
      console.error("Error reactivating inspection task:", serializeError(error));
      throw error;
    }
  },

  /**
   * Assign the auto-created inspection task for a service request to an employee.
   * Matched by service_request_id + task_type, company-scoped. Returns true if a
   * task row was updated; false when the request has no inspection task yet (a
   * legacy request that predates the auto-create trigger) so the caller can fall
   * back to creating one.
   *
   * Track-E slice #3: Nest-only via `POST /tasks/assign-inspection` (which uses
   * service_role — no SELECT RLS dependency).
   *
   * Supabase fallback retired: its `.update().select("id")` (UPDATE ...
   * RETURNING) 42501s post-20260607220000 — the "correct when RLS is still
   * active on local dev" assumption was wrong, local applies the same
   * retirement migration.
   */
  async assignInspectionForRequest(
    requestId: string,
    companyId: string,
    fields: { assignedTo: string; dueDate?: string | null; notes?: string | null },
  ): Promise<boolean> {
    const result = await nestRequest<{ updated: boolean }>(
      API_ROUTES.tasks.assignInspection(),
      {
        method: "POST",
        body: JSON.stringify({
          requestId,
          companyId,
          ...fields,
        }),
      },
    );
    return result.updated;
  },

  // ── tasks: listing counts ────────────────────────────────────────────────────

  /** Task-type rows for a single listing (caller tallies). Company-scoped.
   *  Flipped to Nest `GET /tasks/listing/:listingId/types` (Track-E slice #3). */
  async listListingTaskTypes(listingId: string, companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.byListing(listingId));
        return rows as { task_type: string }[];
      } catch (err) {
        reportNestFallback("tasks.listListingTaskTypes", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select("task_type")
      .eq("company_id", companyId)
      .eq("listing_id", listingId)
      .in("task_type", ["contract_request", "visit_request", "inquiry"]);
    if (error) {
      console.error("Error fetching listing task counts:", serializeError(error));
      throw error;
    }
    return data ?? [];
  },

  /** Task-type rows for ALL listings (caller buckets by listing_id). Company-scoped.
   *  Flipped to Nest `GET /tasks/listing-counts` (Track-E slice #3). */
  async listAllListingTaskTypes(companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.listingCounts());
        return rows as { listing_id: string; task_type: string }[];
      } catch (err) {
        reportNestFallback("tasks.listAllListingTaskTypes", err);
      }
    }
    const { data, error } = await supabase
      .from("tasks")
      .select("listing_id, task_type")
      .eq("company_id", companyId)
      .in("task_type", ["contract_request", "visit_request", "inquiry"])
      .not("listing_id", "is", null);
    if (error) {
      console.error("Error fetching all listing task counts:", serializeError(error));
      throw error;
    }
    return data ?? [];
  },

  // ── task_activity (APPEND-ONLY: insert + list ONLY) ──────────────────────────

  /**
   * List a task's activity timeline (newest first). Scoped through the parent
   * task's company_id via an inner join (task_activity has no own company_id).
   * Append-only audit — NO update/delete counterpart exists by design.
   *
   * Track-E slice #3: flipped to Nest `GET /tasks/:id/activity`.
   */
  async listActivity(taskId: string, companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.tasks.activity(taskId));
        return rows as Record<string, unknown>[];
      } catch (err) {
        reportNestFallback("tasks.listActivity", err);
      }
    }
    const { data, error } = await supabase
      .from("task_activity")
      .select(`
        id, task_id, actor_id, kind, payload, created_at,
        actor:profiles!task_activity_actor_id_fkey(id, full_name, full_name_en),
        task:tasks!inner(company_id)
      `)
      .eq("task_id", taskId)
      .eq("task.company_id", companyId)
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching task activity:", serializeError(error));
      throw error;
    }
    return data ?? [];
  },

  /**
   * Append a single activity entry. Append-only — there is no update or delete
   * path. The parent task it references is already company-scoped by the caller.
   *
   * Track-E slice #3: Nest-only via `POST /tasks/:id/activity`.
   *
   * Supabase fallback retired: its `.insert().select()` (INSERT ... RETURNING)
   * 42501s — task_activity has no non-super-admin SELECT policy.
   *
   * No `actor_id` in the input: the guarded endpoint writes it from the
   * authenticated session (`req.user.id` — see apps/api tasks.controller
   * insertActivity), never from the body, so the caller can't spoof attribution.
   */
  async insertActivity(input: { task_id: string; kind: string; payload: Json }) {
    return nestRequest<unknown>(API_ROUTES.tasks.activity(input.task_id), {
      method: "POST",
      body: JSON.stringify({ kind: input.kind, payload: input.payload }),
    });
  },

  // ── task_type_costs ──────────────────────────────────────────────────────────

  /**
   * Per-company payout cost rows. Company-scoped. The table isn't in the
   * generated Supabase types yet, so cast at the boundary (baselined pattern,
   * same as the original hook).
   */
  async listTaskTypeCosts(companyId: string): Promise<TaskTypeCostRow[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from("task_type_costs")
      .select("*")
      .eq("company_id", companyId)
      .order("task_type");
    if (error) {
      // A 42501 denial is either the guard working as designed or a dead-session
      // anon read — both are owned by QueryCache.onError (denial warning / session
      // heal). console.error would re-report it as an unrelated Sentry error via
      // captureConsoleIntegration (#3443).
      if (!isRpcPermissionDenialError(error)) {
        console.error("Error fetching task type costs:", serializeError(error));
      }
      throw error;
    }
    return (data ?? []) as TaskTypeCostRow[];
  },

  /**
   * Upsert payout-cost rows keyed on (company_id, task_type). An `is_active:false`
   * row is the soft "disabled" toggle — there is no hard delete for these.
   */
  async upsertTaskTypeCosts(rows: TaskTypeCostUpsertRow[]): Promise<TaskTypeCostRow[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from("task_type_costs")
      .upsert(rows, { onConflict: "company_id,task_type" })
      .select();
    if (error) {
      console.error("Error upserting task type costs:", serializeError(error));
      throw error;
    }
    return (data ?? []) as TaskTypeCostRow[];
  },

  // ── visits ───────────────────────────────────────────────────────────────────

  /** List visits, company-scoped, optionally filtered by unit/property. */
  async listVisits(companyId: string, opts: { unitId?: string; propertyId?: string } = {}) {
    let query = supabase
      .from("visits")
      .select("*")
      .eq("company_id", companyId)
      .order("visit_date", { ascending: false });
    if (opts.unitId) {
      query = query.eq("unit_id", opts.unitId);
    } else if (opts.propertyId) {
      query = query.eq("property_id", opts.propertyId);
    }
    const { data, error } = await query;
    if (error) {
      console.error("Error fetching visits:", serializeError(error));
      throw error;
    }
    return (data ?? []) as VisitRow[];
  },

  /**
   * Owner-portal visit list (S9) — visits on the authenticated owner's OWN
   * properties/units, read-only. Nest-first via the auth-only
   * `GET /tasks/visits/owner` (the server resolves the owner PERSON-WIDE —
   * every owners row matching the caller's user_id or shared national_id —
   * then widens to properties.owner_id ∪ contract-derived ∪ units.owner_id).
   * Supabase fallback filters by the caller-derived property ids
   * (contract-derived, primary owner row only) — an acceptable degradation on
   * the rare fallback path, and subject to the visits RLS. The ids are
   * resolved LAZILY (callback) so the happy Nest path never pays for the
   * extra contracts query.
   */
  async listOwnerVisits(
    companyId: string,
    getFallbackPropertyIds: () => Promise<string[]>,
  ): Promise<OwnerVisitRow[]> {
    if (API_URL) {
      try {
        return await nestRequest<OwnerVisitRow[]>(API_ROUTES.tasks.ownerVisits());
      } catch (err) {
        reportNestFallback("tasks.listOwnerVisits", err);
      }
    }

    const fallbackPropertyIds = await getFallbackPropertyIds();
    if (fallbackPropertyIds.length === 0) return [];
    const { data, error } = await supabase
      .from("visits")
      .select("*, property:properties(name, name_en), unit:units(unit_number)")
      .eq("company_id", companyId)
      .in("property_id", fallbackPropertyIds)
      .order("visit_date", { ascending: false });
    if (error) {
      console.error("Error fetching owner visits:", serializeError(error));
      throw error;
    }
    return (data ?? []) as unknown as OwnerVisitRow[];
  },

  /** Lightweight status-only visit list for dashboard stats. Company-scoped. */
  /**
   * @deprecated Use `visitStatusCounts` instead — it issues server-side
   * `count: 'exact', head: true` bucket queries (O(1) body) rather than
   * returning all visit rows (O(N) body). Kept for callers that need raw rows.
   */
  async listVisitStatusesForStats(companyId: string) {
    const { data, error } = await supabase
      .from("visits")
      .select("id, status")
      .eq("company_id", companyId);
    if (error) {
      console.error("Error fetching visit stats:", serializeError(error));
      throw error;
    }
    return data ?? [];
  },

  /**
   * Dashboard visit-stats counts — four server-side bucket queries returning
   * only a `Content-Range` header count (zero row body). Replaces
   * `listVisitStatusesForStats` for the dashboard widget: payload drops from
   * O(N rows) to O(1) integers.
   *
   * Bucket definitions (mirror the JS-side logic in useVisitRequestsStats):
   *   completed — status = 'completed'
   *   scheduled — status IN ('scheduled', 'confirmed', 'pending')
   *   cancelled — status = 'cancelled'
   *   total     — all visits for the company
   */
  async visitStatusCounts(
    companyId: string,
    range?: { startDate?: string; endDate?: string },
  ): Promise<{
    total: number;
    completed: number;
    scheduled: number;
    cancelled: number;
  }> {
    const baseQuery = () => {
      let q = supabase
        .from("visits")
        .select("*", { count: "exact", head: true })
        .eq("company_id", companyId);
      if (range?.startDate) q = q.gte("created_at", range.startDate);
      if (range?.endDate) q = q.lte("created_at", range.endDate);
      return q;
    };

    const [totalRes, completedRes, scheduledRes, cancelledRes] = await Promise.all([
      baseQuery(),
      baseQuery().eq("status", "completed"),
      baseQuery().in("status", ["scheduled", "confirmed", "pending"]),
      baseQuery().eq("status", "cancelled"),
    ]);

    for (const { error } of [totalRes, completedRes, scheduledRes, cancelledRes]) {
      if (error) {
        console.error("Error fetching visit status counts:", serializeError(error));
        throw error;
      }
    }

    return {
      total: totalRes.count ?? 0,
      completed: completedRes.count ?? 0,
      scheduled: scheduledRes.count ?? 0,
      cancelled: cancelledRes.count ?? 0,
    };
  },

  /** Insert a visit, company_id injected. Returns the created row. */
  async createVisit(companyId: string, data: Omit<VisitInsert, "company_id">) {
    const { data: visit, error } = await supabase
      .from("visits")
      .insert({ ...data, company_id: companyId })
      .select()
      .single();
    if (error) {
      console.error("Error creating visit:", serializeError(error));
      throw error;
    }
    return visit as VisitRow;
  },

  /** Update a visit, company-scoped. */
  async updateVisit(id: string, companyId: string, data: VisitUpdate) {
    const { error } = await supabase
      .from("visits")
      .update(data)
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) {
      console.error("Error updating visit:", serializeError(error));
      throw error;
    }
  },

  /** Hard delete a visit, company-scoped (no `deleted_at` column → hard delete). */
  async deleteVisit(id: string, companyId: string) {
    const { error } = await supabase
      .from("visits")
      .delete()
      .eq("id", id)
      .eq("company_id", companyId);
    if (error) {
      console.error("Error deleting visit:", serializeError(error));
      throw error;
    }
  },

  // ── PUBLIC, unauthenticated token RPCs (KEPT on Supabase — no JWT) ────────────

  /**
   * Resolve a service request from a public visit-schedule token. UNAUTHENTICATED
   * — the token is the capability; there is no session/company. KEPT on Supabase
   * (a guarded Nest call would 401). Returns null if the token resolves to nothing.
   */
  async getServiceRequestByToken(token: string): Promise<ServiceRequestByToken | null> {
    const { data, error } = await supabase.rpc("get_service_request_by_token", {
      check_token: token,
    });
    if (error) {
      console.error("Error resolving service request token:", serializeError(error));
      throw error;
    }
    if (!data || data.length === 0) return null;
    return serviceRequestByTokenSchema.parse(data[0]);
  },

  /**
   * Submit a lead (visit/contract/inquiry) from a public listing page.
   * UNAUTHENTICATED — `POST /tasks/public/lead` (ThrottlerGuard, no JWT).
   * The body names only the listing, the lead kind and the visitor's own
   * contact fields; the server derives tenant linkage, title, description and
   * priority from the ACTIVE listing row (never trusts the anonymous body).
   * Input is the shared `createPublicLeadInputSchema` contract (convention
   * §11) — parsed at this boundary so drift from the Nest DTO surfaces here,
   * not as an opaque 400. No Supabase fallback — the authenticated `create()`
   * path is wrong for anonymous callers.
   */
  async createLead(data: CreatePublicLeadInput) {
    const payload = createPublicLeadInputSchema.parse(data);
    await nestRequestPublic<unknown>(API_ROUTES.tasks.createLead(), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /**
   * Submit a visit schedule against a public token. UNAUTHENTICATED — KEPT on
   * Supabase. Forwards the form fields to the atomic `submit_visit_schedule` RPC
   * (which owns the visit/task side-effects in one transaction).
   */
  async submitVisitSchedule(input: {
    token: string;
    scheduledDate: string;
    scheduledTime: string;
    propertyNumber?: string | null;
    unitNumber?: string | null;
    accessMethod?: string | null;
    accessCode?: string | null;
    representativeName?: string | null;
    representativePhone?: string | null;
  }) {
    const { data, error } = await supabase.rpc("submit_visit_schedule", {
      p_token: input.token,
      p_scheduled_date: input.scheduledDate,
      p_scheduled_time: input.scheduledTime,
      p_property_number: input.propertyNumber ?? undefined,
      p_unit_number: input.unitNumber ?? undefined,
      p_access_method: input.accessMethod ?? undefined,
      p_access_code: input.accessCode ?? undefined,
      p_representative_name: input.representativeName ?? undefined,
      p_representative_phone: input.representativePhone ?? undefined,
    });
    if (error) {
      console.error("Error submitting visit schedule:", serializeError(error));
      throw error;
    }
    if (!data) throw new Error("Failed to submit schedule");
    return data;
  },
};

// Re-export the parsed-row schemas for callers needing the shared types.
export {
  taskSchema,
  taskActivitySchema,
  taskTypeCostSchema,
  visitSchema,
  serviceRequestByTokenSchema,
};
